import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiResponse } from '@/types/api-types';
import type { paths } from '~/generated/inis/api-schema';
import { DEFAULT_POLLING_INTERVAL } from '@/consts/app-config';
import { nonnullable, zodRemoveNullable } from '@/lib/zod-helper';
import { inisApi } from '@/services';
import {
  dailyCareHomeResSchema,
  dailyCareIntakeOutputSummaryDtoSchema,
  dailyCareIoItemDtoSchema,
  dailyCareMedicationDtoSchema,
  dailyCarePatientTodoDtoSchema,
  dailyCareScheduledDoseDtoSchema,
  dailyCareShiftFormTodoDtoSchema,
  dailyCareShiftStatusDtoSchema,
  dailyCareTaskBoardItemDtoSchema,
  dailyCareTimelineItemDtoSchema,
  dailyCareVitalSignItemDtoSchema,
  dailyCareVitalSignsDtoSchema,
  dailyCareVitalSlotDtoSchema,
} from '~/generated/inis/zod-schema';

const API_PATH = '/v1/dailyCare/queryDailyCareHome' as const;

export const QUERY_DAILY_CARE_HOME_KEY = API_PATH;

const patientTodoSchema = zodRemoveNullable(dailyCarePatientTodoDtoSchema, {
  includeMask: {
    id: true,
    completeFlag: true,
  },
});

const shiftFormTodosSchema = zodRemoveNullable(
  dailyCareShiftFormTodoDtoSchema,
  {
    excludeMask: {
      effectiveDatetime: true,
    },
  },
);

const timelineGroup = z.object({
  datetime: z.string(),
  items: z.array(
    zodRemoveNullable(dailyCareTimelineItemDtoSchema, {
      excludeMask: {
        refId: true,
      },
    }),
  ),
});

export type TimelineGroup = z.infer<typeof timelineGroup>;

const timelineSchema = z.partialRecord(
  nonnullable(dailyCareShiftStatusDtoSchema.shape.shiftPeriod),
  z.array(timelineGroup),
);

const medicationSchema = zodRemoveNullable(dailyCareMedicationDtoSchema, {
  excludeMask: {
    instructionMemo: true,
    prnNextDatetime: true,
    timingCode: true,
    days: true,
  },
}).extend({
  scheduledDoses: z.array(
    zodRemoveNullable(dailyCareScheduledDoseDtoSchema, {
      excludeMask: {
        administrationId: true,
        effectiveDatetime: true,
        scheduledDatetime: true,
      },
    }),
  ),
});

const taskBoardItemSchema = zodRemoveNullable(dailyCareTaskBoardItemDtoSchema);

const vitalSignItemSchema = zodRemoveNullable(dailyCareVitalSignItemDtoSchema, {
  includeMask: {
    observationCode: true,
  },
});

const vitalSlotSchema = zodRemoveNullable(dailyCareVitalSlotDtoSchema, {
  excludeMask: {
    scheduledDatetime: true,
    effectiveDatetime: true,
  },
});

const vitalSignsSchema = zodRemoveNullable(dailyCareVitalSignsDtoSchema, {
  excludeMask: {
    obstetricRecords: true,
  },
}).extend({
  vitalSlots: z.array(vitalSlotSchema),
  items: z.array(vitalSignItemSchema),
});

const ioItemSchema = zodRemoveNullable(dailyCareIoItemDtoSchema, {
  includeMask: {
    observationCode: true,
  },
});

const intakeOutputSummarySchema = zodRemoveNullable(
  dailyCareIntakeOutputSummaryDtoSchema,
).extend({
  shiftStatuses: z.array(
    zodRemoveNullable(dailyCareShiftStatusDtoSchema, {
      excludeMask: {
        batchId: true,
      },
    }),
  ),
  intake: z.array(ioItemSchema),
  output: z.array(ioItemSchema),
});

const apiResponseSchema = zodRemoveNullable(dailyCareHomeResSchema).extend({
  patientTodos: z.array(patientTodoSchema),
  shiftFormTodos: z.array(shiftFormTodosSchema),
  timeline: timelineSchema,
  medications: z.array(medicationSchema),
  taskBoardItems: z.array(taskBoardItemSchema),
  vitalSigns: vitalSignsSchema,
  intakeOutputSummary: intakeOutputSummarySchema,
});

export type QueryDailyCareHomeData = z.infer<typeof apiResponseSchema>;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

export type DailyCareHomeQueryParams = {
  encounterId: string;
  targetDatetime: string;
  layout: 'SHIFT' | 'OVERVIEW';
};

const queryFn = async (
  params: DailyCareHomeQueryParams,
  signal: AbortSignal,
) => {
  const { data: response } = await inisApi.get<APIResponse>(API_PATH, {
    params,
    signal,
  });

  return apiResponseSchema.parse(response.data);
};

export const getSuspenseQueryDailyCareHomeOptions = (
  params: DailyCareHomeQueryParams,
) => {
  return queryOptions({
    queryKey: [QUERY_DAILY_CARE_HOME_KEY, params],
    queryFn: ({ signal }) => queryFn(params, signal),
    refetchInterval: DEFAULT_POLLING_INTERVAL,
  });
};

export const useSuspenseQueryDailyCareHome = (
  params: DailyCareHomeQueryParams,
) => {
  return useSuspenseQuery(getSuspenseQueryDailyCareHomeOptions(params));
};
